import type { Getter, Setter } from 'jotai';
import type { UndoRedoActionCommand } from '../../../../../modernization-common/undo-redo/types';
import type { AnnotationT } from '../../../../labelling/types';
import { BoundaryType } from '../../../interpolation-manager/types';
import type { SpanT } from '../../../studio-store/types';
import { updateDynamicAnswerSegments } from '../../../studio-store/utils/dynamic-answer-utils';
import { upsertSpan } from '../../../studio-store/utils/sort-upsert-frame-index-to-span';
import { snapshotAnnotationFrames, toFrameWriteEntries } from '../../../utils/annotation-frame-snapshot';
import { annotationLabelsAtom } from '../../annotation-atoms';
import { interpolationManagerAtom } from '../../interpolation';
import { studioStoreAtom } from '../../studio-store';
import { STUDIO_WRITE_ACTIONS } from '../../studio-store-write/actions';
import { dispatchStudioStoreWriteAtom } from '../../studio-store-write/dispatch-studio-store-write';
import type { UpdateAllAnnotationFrameSpanEventPayload } from '../events';
import { reviseDynamicAnswers } from './dynamic-answers-mutation';
export const bulkUpdateAnnotationCommandFactory = (get: Getter, set: Setter): UndoRedoActionCommand<UpdateAllAnnotationFrameSpanEventPayload, void> => {
  let previousSpans: SpanT[] = [];
  let preservedFramesByIndex: Record<number, AnnotationT> = {};
  let newTailStart = 0;
  let restoreDynamicAnswers: () => void = () => {};
  return {
    execute: async payload => {
      const {
        annotationIdx,
        startFrame,
        endFrame
      } = payload;
      const studioStore = get(studioStoreAtom);
      const interpolationManager = get(interpolationManagerAtom);
      if (!studioStore || !interpolationManager) {
        return;
      }
      const metadata = studioStore.getAnnotationMetadata().get(annotationIdx);
      const existingSpans = metadata?.spans ?? [];
      previousSpans = existingSpans;
      const growingSpan = existingSpans.find(span => span.start === startFrame);
      const previousEnd = growingSpan?.end ?? startFrame;
      newTailStart = previousEnd + 1;
      restoreDynamicAnswers = reviseDynamicAnswers(set, annotationIdx, metadata?.dynamicAnswers, current => {
        const lastAnswers = current.find(segment => segment.frameSpan.start <= previousEnd && previousEnd <= segment.frameSpan.end)?.answers;
        return lastAnswers && newTailStart <= endFrame ? updateDynamicAnswerSegments(current, {
          frameSpan: {
            start: newTailStart,
            end: endFrame
          },
          answers: lastAnswers
        }) : undefined;
      });
      const annotationLabels = get(annotationLabelsAtom);
      const [snapshot, startFramePayload] = await Promise.all([metadata ? snapshotAnnotationFrames(studioStore, metadata, annotationLabels, annotationIdx, startFrame + 1, previousEnd) : Promise.resolve({}), studioStore.getAnnotationFrameAtIndex(startFrame, annotationIdx)]);
      preservedFramesByIndex = snapshot;
      const startGeometry = startFramePayload.geometry;
      await interpolationManager.enqueue({
        annotationId: annotationIdx,
        startFrame: {
          geometry: startGeometry,
          index: startFrame,
          type: BoundaryType.KeyFrame
        },
        endFrame: {
          geometry: startGeometry,
          index: endFrame,
          type: BoundaryType.SpanBoundary
        }
      });
      const mergedSpans = upsertSpan(existingSpans, {
        start: startFrame,
        end: endFrame
      });
      set(dispatchStudioStoreWriteAtom, {
        type: STUDIO_WRITE_ACTIONS.UPDATE_METADATA,
        annotationId: annotationIdx,
        partialPayload: {
          spans: mergedSpans
        }
      });
    },
    undo: payload => {
      const {
        annotationIdx,
        endFrame
      } = payload;
      if (newTailStart <= endFrame) {
        const deletes = Array.from({
          length: endFrame - newTailStart + 1
        }, (_, i) => ({
          frameIndex: newTailStart + i,
          annotationId: annotationIdx
        }));
        set(dispatchStudioStoreWriteAtom, {
          type: STUDIO_WRITE_ACTIONS.BULK_DELETE,
          deletes
        });
      }
      const writes = toFrameWriteEntries(annotationIdx, preservedFramesByIndex);
      if (writes.length > 0) {
        set(dispatchStudioStoreWriteAtom, {
          type: STUDIO_WRITE_ACTIONS.BULK_WRITE,
          writes
        });
      }
      set(dispatchStudioStoreWriteAtom, {
        type: STUDIO_WRITE_ACTIONS.UPDATE_METADATA,
        annotationId: annotationIdx,
        partialPayload: {
          spans: previousSpans
        }
      });
      restoreDynamicAnswers();
    }
  };
};